import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../lib/scroll";

const HERO_IMG = "/uploads/hero.jpg";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);
  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section id="home" data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Rope access works"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-[linear-gradient(to_top,#050505,transparent)]" />
      </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-[1500px] px-5 pb-10 pt-40 md:px-10">
        <motion.img
          src="/logo.png"
          alt="PROALP22 — alpinismo industrial"
          data-testid="hero-logo"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10 w-96 md:w-[34rem] drop-shadow-[0_10px_40px_rgba(0,0,0,0.8)]"
        />
        <motion.p
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="hero-kicker"
          className="mb-6 flex items-center gap-3 text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-neutral-300"
        >
          <span className="h-px w-10 bg-[#FF5A00]" />
          {t.hero.kicker}
        </motion.p>

        <h1 data-testid="hero-title" className="font-display uppercase leading-[0.95] tracking-wide">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block text-[17vw] sm:text-8xl lg:text-[9rem] ${
                  i === 1 ? "text-[#FF5A00]" : i === 2 ? "text-outline" : "text-white"
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            data-testid="hero-subtitle"
            className="max-w-xl text-sm md:text-lg text-neutral-300"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-consult-btn"
              onClick={() => scrollToId("#contacts")}
              className="group flex items-center gap-3 bg-[#FF5A00] px-7 py-4 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04F00]"
            >
              {t.hero.cta1}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-projects-btn"
              onClick={() => scrollToId("#projects")}
              className="group flex items-center gap-3 border border-white/25 px-7 py-4 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-[#FF5A00] hover:text-[#FF5A00]"
            >
              {t.hero.cta2}
            </button>
          </motion.div>
        </div>

        <motion.button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollToId("#what-we-do")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-10 hidden md:flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-neutral-500 transition-colors hover:text-[#FF5A00]"
        >
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown size={14} />
          </motion.span>
          {t.hero.scroll}
        </motion.button>
      </div>
    </section>
  );
};
